/**
 * P80 Slideshow - Performant CSS-based slider
 *
 * HTML Attributes:
 * - data-auto-slide="true|false" - Enable/disable auto-sliding (default: false)
 * - data-slide-speed="3000" - Slide interval in milliseconds (default: 5000)
 *
 */

class P80Slideshow {
  constructor(element) {
    this.slider = element;
    this.list = this.slider.querySelector(".p80-slideshow__list");
    this.items = this.slider.querySelectorAll(".p80-slideshow__item");
    this.prevBtn = this.slider.querySelector(".p80-slideshow__nav--prev");
    this.nextBtn = this.slider.querySelector(".p80-slideshow__nav--next");
    this.pagingDots = this.slider.querySelectorAll(
      ".p80-slideshow__paging-dot"
    );

    this.currentIndex = 0;
    this.totalSlides = this.items.length;
    this.autoSlideInterval = null;

    // Get options from data attributes
    this.autoSlide = this.slider.dataset.autoSlide === "true";
    this.slideSpeed = parseInt(this.slider.dataset.slideSpeed) || 5000;

    this.init();
  }

  init() {
    if (this.totalSlides <= 1) {
      this.hideControls();
      return;
    }

    // Make all slides visible after initialization (prevent CLS)
    this.items.forEach((item) => {
      item.style.visibility = "visible";
    });

    // Setup event listeners
    this.prevBtn?.addEventListener("click", () => this.goToPrev());
    this.nextBtn?.addEventListener("click", () => this.goToNext());

    this.pagingDots.forEach((dot, index) => {
      dot.addEventListener("click", () => this.goToSlide(index));
    });

    // Touch support for mobile
    this.setupTouchEvents();

    // Keyboard navigation
    this.slider.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") this.goToPrev();
      if (e.key === "ArrowRight") this.goToNext();
    });

    // Update initial state
    this.updateUI();

    // Start auto-slide if enabled
    if (this.autoSlide) {
      this.startAutoSlide();

      // Pause on hover
      this.slider.addEventListener("mouseenter", () => this.stopAutoSlide());
      this.slider.addEventListener("mouseleave", () => this.startAutoSlide());
    }
  }

  setupTouchEvents() {
    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    this.slider.addEventListener(
      "touchstart",
      (e) => {
        startX = e.touches[0].clientX;
        isDragging = true;
        this.stopAutoSlide();
      },
      { passive: true }
    );

    this.slider.addEventListener(
      "touchmove",
      (e) => {
        if (!isDragging) return;
        currentX = e.touches[0].clientX;
      },
      { passive: true }
    );

    this.slider.addEventListener("touchend", () => {
      if (!isDragging) return;

      const diff = startX - currentX;
      const threshold = 50; // Minimum swipe distance

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.goToNext();
        } else {
          this.goToPrev();
        }
      }

      isDragging = false;
      if (this.autoSlide) this.startAutoSlide();
    });
  }

  goToSlide(index) {
    if (index < 0 || index >= this.totalSlides) return;

    this.currentIndex = index;

    // Use CSS transform for hardware acceleration
    const offset = -this.currentIndex * 100;
    this.list.style.transform = `translateX(${offset}%)`;

    this.updateUI();
    this.resetAutoSlide();
  }

  goToNext() {
    const nextIndex = this.currentIndex + 1;
    if (nextIndex < this.totalSlides) {
      this.goToSlide(nextIndex);
    }
  }

  goToPrev() {
    const prevIndex = this.currentIndex - 1;
    if (prevIndex >= 0) {
      this.goToSlide(prevIndex);
    }
  }

  updateUI() {
    // Update navigation buttons state
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
      this.prevBtn.setAttribute("aria-disabled", this.currentIndex === 0);
    }

    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex === this.totalSlides - 1;
      this.nextBtn.setAttribute(
        "aria-disabled",
        this.currentIndex === this.totalSlides - 1
      );
    }

    // Update paging dots
    this.pagingDots.forEach((dot, index) => {
      dot.classList.toggle("is-active", index === this.currentIndex);
      dot.setAttribute("aria-current", index === this.currentIndex);
    });

    // Update aria-live region for screen readers
    this.slider.setAttribute(
      "aria-label",
      `Slide ${this.currentIndex + 1} of ${this.totalSlides}`
    );
  }

  startAutoSlide() {
    if (!this.autoSlide) return;

    this.stopAutoSlide();
    this.autoSlideInterval = setInterval(() => {
      if (this.currentIndex < this.totalSlides - 1) {
        this.goToNext();
      } else {
        this.goToSlide(0); // Loop back to start
      }
    }, this.slideSpeed);
  }

  stopAutoSlide() {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
      this.autoSlideInterval = null;
    }
  }

  resetAutoSlide() {
    if (this.autoSlide) {
      this.stopAutoSlide();
      this.startAutoSlide();
    }
  }

  hideControls() {
    if (this.prevBtn) this.prevBtn.style.display = "none";
    if (this.nextBtn) this.nextBtn.style.display = "none";
    const paging = this.slider.querySelector(".p80-slideshow__paging");
    if (paging) paging.style.display = "none";
  }

  destroy() {
    this.stopAutoSlide();
    // Remove event listeners and reset state if needed
  }
}

// Auto-initialize all sliders on page load
document.addEventListener("DOMContentLoaded", () => {
  const sliders = document.querySelectorAll(".p80-slideshow");
  sliders.forEach((slider) => new P80Slideshow(slider));
});

// Export for module usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = P80Slideshow;
}
